import { useEffect, useMemo, useState } from "react";
import { Skeleton } from "@mui/material";
import { GridRowId, GridRowSelectionModel } from "@mui/x-data-grid";
import { useSetAtom } from "jotai";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "../../../../../../common/routesUtils";
import ServerTable from "../../../../../../components/common/table/ServerTable";
import { useServerTableController } from "../../../../../../components/common/table/ServerTable/ServerDataGrid/useServerTableController";
import { CustomColumnMenu } from "../../../../../../components/common/table/TableData/CustomColumnMenu";
import { CustomDataGridFooter } from "../../../../../../components/common/table/TableData/TableDataFooter";
import TableSelectActions from "../../../../../../components/common/table/TableData/TableSelectActions";
import { useCustomTableDataStyles } from "../../../../../../components/common/table/TableData/useCustomTableDataStyles";
import { PageType } from "../../../../../../config/page.types";
import { useBreadcrumbs } from "../../../../../../context/BreadcrumbsProvider";
import { useFiltersRegistry } from "../../../../../../context/FiltersProvider";
import { usePageConfigurationState } from "../../../../../../context/PageConfigrations/usePageConfigurationState";
import { NodesPageDTO, ResultsItemsListPayload } from "../../../../../../datasources/api-client/data-contracts";
import { ApiRoutes } from "../../../../../../datasources/networking/apiRoutes";
import { useReadResource, useWriteResource } from "../../../../../../hooks/useResource";
import { useCurrentTheme } from "../../../../../../theme/theme";
import CustomNoRowsOverlay from "../../../../../results/ResultsTransport/TransportGreenrouter/components/CustomNoRowsOverlay";
import { colsData, getColumns, getRows } from "./data";

export const OperationalUnitsList = () => {
	const { t } = useTranslation(["common", "feature.units"]);
	const navigate = useNavigate();
	const { visitPage } = useBreadcrumbs();

	const tableSx = useCustomTableDataStyles();

	const { appliedFilters } = useFiltersRegistry();

	// config state in local storage
	const [config, setConfig] = usePageConfigurationState(PageType.DataCollectionConfigurationOperationalUnits, {
		tabValue: "list",
		page: 0,
		pageSize: 10,
		sortField: undefined,
		sortDirection: undefined,
		columnVisibilityModel: {},
	});

	// generic table controller
	const {
		paginationModel,
		handlePaginationChange,
		sortModel,
		handleSortModelChange,
		columnVisibilityModel,
		handleColumnVisibilityChange,
	} = useServerTableController({
		config,
		setConfig,
	});

	// api payload
	const payload = useMemo(() => ({ ...appliedFilters.payload }), [appliedFilters]);

	const { data, isLoading, reload } = useReadResource<ResultsItemsListPayload, NodesPageDTO>(
		{
			route: ApiRoutes.ResultGreerouterSitesList,
			query: {
				page: paginationModel.page,
				pageSize: paginationModel.pageSize,
				...(sortModel?.[0]?.field && { sortBy: sortModel[0].field }),
				...(sortModel?.[0]?.sort && { sortDir: sortModel[0].sort }),
			},
		},
		{
			readMethod: "post",
		},
		payload
	);

	// delete
	const { put: deleteNode, isLoading: deleteNodeLoading } = useWriteResource(
		{ route: ApiRoutes.ConfigurationDeleteNodes },
		{ subjectKey: "nodes", overwriteErrorMessage: true }
	);

	const [selectedRowIds, setSelectedRowIds] = useState<GridRowSelectionModel>({
		type: "include",
		ids: new Set<GridRowId>(),
	});

	const appliedFiltersKey = useMemo(() => JSON.stringify(appliedFilters), [appliedFilters]);

	useEffect(() => {
		setSelectedRowIds({ type: "include", ids: new Set() });

		if (config.page !== 0) {
			setConfig({ page: 0 });
		}
	}, [appliedFiltersKey]);

	const rows = useMemo(() => (data?.items ? getRows(data) : []), [data]);

	const handleRowSelection = (newSelection: GridRowSelectionModel) => {
		setSelectedRowIds({
			type: newSelection.type,
			ids: new Set(newSelection.ids),
		});
	};

	const selectedRowsId: GridRowId[] = useMemo(() => {
		if (selectedRowIds.type === "include") {
			return Array.from(selectedRowIds.ids);
		}

		return rows.filter((r) => !selectedRowIds.ids.has(r.id)).map((r) => r.id);
	}, [selectedRowIds, rows]);

	const handleDeleteRows = async () => {
		try {
			await deleteNode({ ids: selectedRowsId }, { actionKey: "delete" });
			setSelectedRowIds({ type: "include", ids: new Set() });
		} catch {}
	};

	// columns
	const textColor = useCurrentTheme().theme.palette.primary.contrastText;

	const columns = useMemo(
		() =>
			getColumns(colsData(), textColor, t, (id, code) => {
				visitPage(PageType.DataCollectionConfigurationOperationalUnitsDetail, false, code, id);
				navigate(AppRoutes.DataCollectionConfigurationOperationalUnitsDetail.replace(":id", String(id)));
			}),
		[textColor, t]
	);

	if (isLoading) {
		return <Skeleton variant="rounded" height="450px" />;
	}

	return (
		<ServerTable
			title={t("feature.units:greenrouterList/greenrouter operating units list")}
			isLoading={isLoading}
			onReload={reload}
			actions={
				data?.itemsCount ? (
					<TableSelectActions
						selectedRowsId={selectedRowsId}
						onDeleteRows={handleDeleteRows}
						deleteLoading={deleteNodeLoading}
					/>
				) : null
			}
			getRowId={(row) => row.id}
			rows={rows}
			columns={columns}
			rowHeight={56}
			checkboxSelection
			rowSelectionModel={selectedRowIds}
			onRowSelectionModelChange={handleRowSelection}
			paginationModel={paginationModel}
			onPaginationModelChange={handlePaginationChange}
			rowCount={data?.itemsCount ?? 0}
			pageSizeOptions={[10, 25, 50]}
			sortModel={sortModel}
			onSortModelChange={handleSortModelChange}
			columnVisibilityModel={columnVisibilityModel}
			onColumnVisibilityModelChange={handleColumnVisibilityChange}
			slots={{
				noRowsOverlay: () => <CustomNoRowsOverlay />,
				footer: CustomDataGridFooter,
				columnMenu: CustomColumnMenu,
			}}
			sx={tableSx}
		/>
	);
};
